import { useCallback, useEffect, useRef, useState } from 'react';

function useAsyncCall<Args extends unknown[], Return>(
  call: (...args: Args) => Promise<Return>,
  onMount: false,
  initialValue?: Return,
): [trigger: (...args: Args) => void, value: Return | null, isLoading: boolean, error: unknown, hasBeenCalled: boolean];
function useAsyncCall<Args extends unknown[], Return>(
  call: (...args: Args | []) => Promise<Return>,
  onMount: true,
  initialValue?: Return,
): [
  trigger: (...args: Args | []) => void,
  value: Return | null,
  isLoading: boolean,
  error: unknown,
  hasBeenCalled: boolean,
];
function useAsyncCall<Args extends unknown[], Return>(
  call: (...args: Args | []) => Promise<Return>,
  onMount: boolean,
  initialValue?: Return,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasBeenCalled, setHasBeenCalled] = useState(false);
  const [value, setValue] = useState<Return | null>(initialValue || null);
  const [error, setError] = useState<unknown>(null);

  const trigger = useCallback(
    (...args: Args | []) => {
      setIsLoading(true);
      call(...args)
        .then((response) => {
          setValue(response);
          setIsLoading(false);
          setHasBeenCalled(true);
        })
        .catch((error) => {
          setHasBeenCalled(true);
          setIsLoading(false);
          setError(error);
        });
    },
    [call, setIsLoading, setValue, setError],
  );

  useEffect(() => {
    if (onMount && !hasBeenCalled) trigger();
  }, [onMount, trigger, hasBeenCalled]);

  return [trigger, value, isLoading, error, hasBeenCalled];
}

function useAsyncCallOnChange<Value>(value: Value, onChange: (value: Value) => void) {
  const call = useRef(onChange);
  useEffect(() => {
    call.current && call.current(value);
  }, [value, call]);
}

type DownloadFormat = 'csv' | 'xlsx' | 'pdf';

function useDownloadApplicationType() {
  return useCallback(
    (format: DownloadFormat) =>
      ({
        csv: 'text/csv;charset=utf-8',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pdf: 'application/pdf',
      })[format],
    [],
  );
}

function useDownloadBlobFormater() {
  return useCallback(
    (format: DownloadFormat) =>
      ({
        csv: (data: Blob) => ['\uFEFF' + data],
        xlsx: (data: Blob) => [data],
        /* pdf exports are not saved but opened in the browser instead */
        pdf: () => {},
      })[format],
    [],
  );
}

function useDownloadToFormat(
  getData: (format: DownloadFormat) => Promise<Blob>,
  getDownloadName: (format: DownloadFormat) => string,
  saveAs: (blob: Blob, name: string) => void,
): (format: DownloadFormat) => Promise<void> {
  const downloadApplicationType = useDownloadApplicationType();
  const downloadBlobFormater = useDownloadBlobFormater();
  const download = useCallback(
    async (format: DownloadFormat) => {
      const data = await getData(format);
      const blobParts = downloadBlobFormater(format)(data);
      if (blobParts) {
        const blob = new Blob(blobParts, {
          type: downloadApplicationType(format),
        });
        saveAs(blob, getDownloadName(format));
      }
    },
    [saveAs, downloadApplicationType, downloadBlobFormater, getData, getDownloadName],
  );

  return download;
}

export const UtilsHooks = {
  useAsyncCall,
  useAsyncCallOnChange,
  useDownloadApplicationType,
  useDownloadBlobFormater,
  useDownloadToFormat,
};
